import { z } from "zod";

/// Wire-shape mirrors of the shore Pulse Survey DTOs. Slice 1 ships the
/// skeleton; later slices flesh out per-endpoint shapes. Per webui T3/T4
/// every API boundary is Zod-validated.

export const PULSE_SURVEY_STATUSES = ["draft", "active", "paused", "archived"] as const;
export const pulseSurveyStatusSchema = z.enum(PULSE_SURVEY_STATUSES);
export type PulseSurveyStatus = z.infer<typeof pulseSurveyStatusSchema>;

export const PULSE_SURVEY_TYPES = ["ai_pulse", "custom", "self_check_in", "event"] as const;
export const pulseSurveyTypeSchema = z.enum(PULSE_SURVEY_TYPES);
export type PulseSurveyType = z.infer<typeof pulseSurveyTypeSchema>;

/// Admin create/update only ever sends the two non-system types.
export const ADMIN_SURVEY_TYPES = ["custom", "event"] as const;
export const adminSurveyTypeSchema = z.enum(ADMIN_SURVEY_TYPES);
export type AdminSurveyType = z.infer<typeof adminSurveyTypeSchema>;

export const PULSE_SURVEY_QUESTION_STRATEGIES = ["fixed", "ai_generated"] as const;
export const pulseSurveyQuestionStrategySchema = z.enum(PULSE_SURVEY_QUESTION_STRATEGIES);
export type PulseSurveyQuestionStrategy = z.infer<typeof pulseSurveyQuestionStrategySchema>;

export const PULSE_SURVEY_RUN_STATUSES = [
  "pending",
  "open",
  "closed",
  "failed",
  "cancelled",
] as const;
export const pulseSurveyRunStatusSchema = z.enum(PULSE_SURVEY_RUN_STATUSES);
export type PulseSurveyRunStatus = z.infer<typeof pulseSurveyRunStatusSchema>;

export const PULSE_SURVEY_RESPONSE_QUESTION_TYPES = [
  "scale",
  "yes_no",
  "multi_select",
  "free_text",
  "mood",
] as const;
export const pulseSurveyResponseQuestionTypeSchema = z.enum(PULSE_SURVEY_RESPONSE_QUESTION_TYPES);
export type PulseSurveyResponseQuestionType = z.infer<typeof pulseSurveyResponseQuestionTypeSchema>;

export const SYSTEM_EVENT_NAMES = [
  "OneOnOneCompleted",
  "FeedbackCreated",
  "KudosCreated",
  "GoalCheckInSubmitted",
  "SelfCheckInSubmitted",
] as const;
export const systemEventNameSchema = z.enum(SYSTEM_EVENT_NAMES);
export type SystemEventName = z.infer<typeof systemEventNameSchema>;

export const PRIVACY_MODES = [
  "private_reflection",
  "anonymous_signal",
  "anonymous_comment",
] as const;
export const privacyModeSchema = z.enum(PRIVACY_MODES);
export type PrivacyMode = z.infer<typeof privacyModeSchema>;

export const AUDIENCE_KINDS = [
  "workspace",
  "departments",
  "subtree",
  "memberships",
  "event_inferred",
] as const;
export const audienceKindSchema = z.enum(AUDIENCE_KINDS);
export type AudienceKind = z.infer<typeof audienceKindSchema>;

/// Health vocabulary schemas (HealthDimension, RiskWedge, StrengthWedge,
/// DimensionWedgeWeight + their list-response shapes) moved to
/// `@/shared/pulse/health/health-vocabulary.schema` in the C1 Phase B
/// refactor, they're consumed by pulse-scoring + pulse-dashboards too.

/// PulseSurvey wire shape, matches shore `PulseSurveyRowDto`. JSON
/// columns accepted as unknown; consumers narrow per surveyType etc.
/// `createdByUserId` is null for system surveys (ai_pulse, self_check_in).
export const pulseSurveyRowSchema = z
  .object({
    id: z.string(),
    workspaceId: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    status: pulseSurveyStatusSchema,
    surveyType: pulseSurveyTypeSchema,
    scheduledConfig: z.unknown(),
    eventConfig: z.unknown(),
    questionStrategy: pulseSurveyQuestionStrategySchema,
    fixedQuestions: z.unknown(),
    aiPromptHints: z.string().nullable(),
    audience: z.unknown(),
    responsesAnonymous: z.boolean(),
    expiresAfterHours: z.number().int(),
    createdByUserId: z.string().nullable(),
    /// Shore-resolved creator, for display. Optional so a webui deployed ahead
    /// of the shore change still parses today's rows: this schema is .strict().
    createdBy: z.object({ userId: z.string(), displayName: z.string() }).nullable().optional(),
    /// Display name of the membership a subtree audience is rooted on, so the
    /// audience can read "Maya Manager's direct reports".
    audienceRootName: z.string().nullable().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    lastRunAt: z.string().nullable().optional(),
    runCount30d: z.number().int().optional(),
    respondentCount30d: z.number().int().optional(),
  })
  .strict();
export type PulseSurveyRow = z.infer<typeof pulseSurveyRowSchema>;

export const listPulseSurveysResponseSchema = z
  .object({
    rows: z.array(pulseSurveyRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type ListPulseSurveysResponse = z.infer<typeof listPulseSurveysResponseSchema>;

export const createPulseSurveySchema = z
  .object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    surveyType: adminSurveyTypeSchema,
    scheduledConfig: z.unknown().optional(),
    eventConfig: z.unknown().optional(),
    questionStrategy: pulseSurveyQuestionStrategySchema,
    fixedQuestions: z.unknown().optional(),
    aiPromptHints: z.string().max(2000).optional(),
    audience: z.unknown(),
    expiresAfterHours: z.number().int().min(1).max(720).optional(),
    /// Anonymity is decided once, at create, for custom surveys. The shore
    /// defaults it to true when omitted and rejects it on update (immutable).
    responsesAnonymous: z.boolean(),
  })
  .strict();
export type CreatePulseSurvey = z.infer<typeof createPulseSurveySchema>;

/// Form-state shape used by the create / edit page (RHF + Zod). Question
/// rows are flattened to text/type/isRequired/id. Audience is intentionally
/// typed as `unknown` so the form-level type can layer the discriminated
/// `AudienceValue` on top without circular imports. `expiresAfterHoursInput`
/// is a string so the input can be empty mid-edit; the page coerces and
/// clamps on submit.
export const pulseSurveyFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, "Give the pulse a title.")
      .max(200, "Keep it under 200 characters."),
    description: z.string().max(2000, "Keep it under 2000 characters."),
    expiresAfterHoursInput: z.string(),
    /// Optional ISO datetime for when the survey first fires. Empty string
    /// means "now + 5 minutes" (legacy default). Validated as a real date
    /// when non-empty by the form-level refinement.
    fireAtInput: z.string(),
    cadence: z.enum(["once", "weekly"]).default("once"),
    audience: z.unknown(),
    /// Anonymous by default. ON = responses stay anonymous; OFF lets workspace
    /// admins read each member's individual named answers. Immutable after
    /// create, the edit form shows it read-only (see PulseSurveyForm).
    responsesAnonymous: z.boolean().default(true),
    questions: z
      .array(
        z.object({
          id: z.string(),
          text: z.string().trim().min(1, "Every question needs text.").max(500),
          type: pulseSurveyResponseQuestionTypeSchema,
          isRequired: z.boolean(),
          options: z
            .array(z.string().trim().min(1).max(80))
            .max(10, "Maximum 10 options")
            .optional(),
        }),
      )
      .min(1, "Add at least one question.")
      .max(15, "Maximum 15 questions per survey."),
  })
  .superRefine((values, ctx) => {
    values.questions.forEach((q, i) => {
      if (q.type === "multi_select") {
        const opts = q.options ?? [];
        if (opts.length < 2) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["questions", i, "options"],
            message: "Add at least 2 options for a multi-select question.",
          });
        }
      }
    });
  });
export type PulseSurveyFormValues = z.infer<typeof pulseSurveyFormSchema>;

export const updatePulseSurveySchema = createPulseSurveySchema.partial();
export type UpdatePulseSurvey = z.infer<typeof updatePulseSurveySchema>;

export const okResponseSchema = z.object({ ok: z.literal(true) }).strict();
export type OkResponse = z.infer<typeof okResponseSchema>;

/// Slice 6, member-response wire shapes.
export const PULSE_SURVEY_DELIVERY_STATUSES = [
  "queued",
  "sent",
  "partial",
  "completed",
  "skipped",
  "failed",
  "expired",
] as const;
export const pulseSurveyDeliveryStatusSchema = z.enum(PULSE_SURVEY_DELIVERY_STATUSES);
export type PulseSurveyDeliveryStatus = z.infer<typeof pulseSurveyDeliveryStatusSchema>;

export const pendingDeliveryRowSchema = z
  .object({
    deliveryId: z.string(),
    surveyId: z.string(),
    surveyTitle: z.string(),
    surveyType: pulseSurveyTypeSchema,
    isAnonymous: z.boolean(),
    questionsSnapshot: z.unknown(),
    channel: z.string(),
    sentAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    respondedQuestionIndexes: z.array(z.number().int()),
    status: pulseSurveyDeliveryStatusSchema,
  })
  .strict();
export type PendingDeliveryRow = z.infer<typeof pendingDeliveryRowSchema>;

export const listPendingDeliveriesResponseSchema = z
  .object({ rows: z.array(pendingDeliveryRowSchema) })
  .strict();
export type ListPendingDeliveriesResponse = z.infer<typeof listPendingDeliveriesResponseSchema>;

export const submitResponseSchema = z
  .object({
    questionRefIndex: z.number().int().min(0),
    responseValue: z.unknown(),
  })
  .strict();
export type SubmitResponse = z.infer<typeof submitResponseSchema>;

export const submitResponseResultSchema = z
  .object({ ok: z.literal(true), status: pulseSurveyDeliveryStatusSchema })
  .strict();
export type SubmitResponseResult = z.infer<typeof submitResponseResultSchema>;

/// Member own-history wire shapes (GET /pulse-surveys/me/history). The author
/// always sees their own raw answers; no one else does. Mirrors the shore
/// OwnHistoryRowDto / OwnHistoryResponseDto in member-response.dto.ts.
export const ownHistoryItemSchema = z
  .object({
    questionRefIndex: z.number().int(),
    questionTextSnapshot: z.string(),
    questionType: pulseSurveyResponseQuestionTypeSchema,
    responseValue: z.unknown(),
    respondedAt: z.string(),
  })
  .strict();
export type OwnHistoryItem = z.infer<typeof ownHistoryItemSchema>;

export const ownHistoryRowSchema = z
  .object({
    deliveryId: z.string(),
    surveyTitle: z.string(),
    surveyType: pulseSurveyTypeSchema,
    firedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    responses: z.array(ownHistoryItemSchema),
  })
  .strict();
export type OwnHistoryRow = z.infer<typeof ownHistoryRowSchema>;

export const ownHistoryResponseSchema = z
  .object({ rows: z.array(ownHistoryRowSchema), nextCursor: z.string().nullable() })
  .strict();
export type OwnHistoryResponse = z.infer<typeof ownHistoryResponseSchema>;
